import {
  Box,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { GUIDED_STEP_ORDER } from "./types";

interface OnboardingProgressMenuItemProps {
  // The in-progress company (carries onboardingCompletedSteps from /companies).
  company: any;
  onClick: () => void;
}

// `track` is never persisted (no company exists yet on that screen), so the user can only
// ever complete the remaining steps. Count against that so a freshly started company reads
// as a real fraction rather than always being short one step it can't record.
const TOTAL_STEPS = GUIDED_STEP_ORDER.length - 1; // exclude "track"

// A small SVG donut that fills clockwise to show fraction complete. Colored with the theme
// primary so it pops in the sidebar; the unfilled remainder uses a muted track color.
const ProgressPie: React.FC<{
  fraction: number;
  size?: number;
  filled: string;
  track: string;
}> = ({ fraction, size = 22, filled, track }) => {
  const r = size / 2;
  const stroke = Math.max(3, size / 3.5);
  const innerR = r - stroke / 2;
  const circumference = 2 * Math.PI * innerR;
  const clamped = Math.max(0, Math.min(1, fraction));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Unfilled track */}
      <circle
        cx={r}
        cy={r}
        r={innerR}
        fill="none"
        stroke={track}
        strokeWidth={stroke}
      />
      {/* Filled arc — rotate -90deg so it starts at 12 o'clock and fills clockwise */}
      <circle
        cx={r}
        cy={r}
        r={innerR}
        fill="none"
        stroke={filled}
        strokeWidth={stroke}
        strokeDasharray={`${circumference * clamped} ${circumference}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${r} ${r})`}
        style={{ transition: "stroke-dasharray 0.4s ease" }}
      />
    </svg>
  );
};

const OnboardingProgressMenuItem: React.FC<OnboardingProgressMenuItemProps> = ({
  company,
  onClick,
}) => {
  const theme = useTheme();

  const completed = Array.isArray(company?.onboardingCompletedSteps)
    ? company.onboardingCompletedSteps.filter((s: string) => s !== "track").length
    : 0;
  const done = Math.min(completed, TOTAL_STEPS);
  const fraction = TOTAL_STEPS > 0 ? done / TOTAL_STEPS : 0;
  const percent = Math.round(fraction * 100);

  const filled = theme.palette.primary.main;
  const filledLight = theme.palette.primary.light;
  const trackColor = theme.palette.action.selected;

  const tooltip = (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
        Onboarding progress
      </Typography>
      <Typography variant="caption" sx={{ display: "block" }}>
        You've completed {done} of {TOTAL_STEPS} setup steps ({percent}%).
      </Typography>
      <Typography variant="caption" sx={{ display: "block", opacity: 0.85, mt: 0.5 }}>
        Click to pick up where you left off
        {company?.name ? ` with ${company.name}` : ""}.
      </Typography>
    </Box>
  );

  return (
    <Tooltip title={tooltip} placement="right" arrow>
      <ListItemButton
        onClick={onClick}
        sx={{
          flexDirection: "column",
          alignItems: "stretch",
          // A subtle tinted background + left accent so it stands out from the plain nav rows.
          backgroundColor: `${filled}14`, // ~8% primary
          borderLeft: `3px solid ${filled}`,
          "&:hover": { backgroundColor: `${filled}22` },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <ListItemIcon sx={{ minWidth: 40 }}>
            <ProgressPie fraction={fraction} filled={filled} track={trackColor} />
          </ListItemIcon>
          <ListItemText
            primary="Onboarding"
            primaryTypographyProps={{ fontWeight: 600 }}
          />
          {/* Step count beside the label so the pie stays a clean donut. */}
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "text.secondary",
              ml: 1,
              whiteSpace: "nowrap",
            }}
          >
            {done}/{TOTAL_STEPS}
          </Typography>
        </Box>
        {/* Gradient progress bar on its own row beneath the label. */}
        <Box sx={{ pl: 5, pr: 1, pb: 0.5, mt: 0.25 }}>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: trackColor,
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                backgroundColor: "transparent",
                backgroundImage: `linear-gradient(90deg, ${filled} 0%, ${filledLight} 100%)`,
              },
            }}
          />
        </Box>
      </ListItemButton>
    </Tooltip>
  );
};

export default OnboardingProgressMenuItem;
